import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';
import { FormikValues } from 'formik';

import api from '../../../utils/api';
import { GET_SCHOOL_CONFIG_QUERY_KEY, SchoolFeatureConfig } from './useGetSchoolConfigQuery';

export type UpdateSchoolConfigPayload = SchoolFeatureConfig;

interface SchoolConfigMutationResponse {
  code: number;
  message: string;
  data?: SchoolFeatureConfig;
}

const parseBooleanValue = (value: unknown) => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }

  return Boolean(value);
};

const parseStringValue = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.join(',');
  }

  return value === null || typeof value === 'undefined' ? '' : String(value);
};

export const useUpdateSchoolConfigMutation = (handleClose?: () => void) => {
  const queryClient = useQueryClient();

  const updateSchoolConfig = async (payload: UpdateSchoolConfigPayload): Promise<SchoolConfigMutationResponse> => {
    return (await api.put('/SchoolFeatureConfig', payload))?.data;
  };

  const mutation = useMutation({
    mutationKey: ['update-school-config'],
    mutationFn: updateSchoolConfig,
  });

  const updateSchoolConfigHandler = async (values: FormikValues, resetForm?: () => void) => {
    const payload: UpdateSchoolConfigPayload = {
      id: Number(values?.id ?? 0),
      abandonedNotificationsEnabled: parseBooleanValue(values?.abandonedNotificationsEnabled),
      abandonedNotificationIntervalDays: Number(values?.abandonedNotificationIntervalDays ?? 0),
      abandonedNotificationMaxReminders: Number(values?.abandonedNotificationMaxReminders ?? 0),
      abandonedNotificationEmailTemplateCode: parseStringValue(values?.abandonedNotificationEmailTemplateCode),
      allowCourseChange: parseBooleanValue(values?.allowCourseChange),
      courseChangeLockStage: Number(values?.courseChangeLockStage ?? 0),
      concentrationSelectionEnabled: parseBooleanValue(values?.concentrationSelectionEnabled),
      allowPostRejectionOverride: parseBooleanValue(values?.allowPostRejectionOverride),
      postRejectionOverrideRoles: parseStringValue(values?.postRejectionOverrideRoles),
      postRejectionTargetStatus: parseStringValue(values?.postRejectionTargetStatus),
      autoProvisionCredentials: parseBooleanValue(values?.autoProvisionCredentials),
      usernameFormat: Number(values?.usernameFormat ?? 0),
      defaultPasswordFormat: Number(values?.defaultPasswordFormat ?? 0),
      generateMatricBeforeMove: parseBooleanValue(values?.generateMatricBeforeMove),
    };

    try {
      await mutation.mutateAsync(payload, {
        onSuccess: (data) => {
          notification.success({
            message: data?.message || 'School config updated successfully',
          });

          queryClient.refetchQueries({
            queryKey: GET_SCHOOL_CONFIG_QUERY_KEY,
          });

          resetForm?.();
          handleClose?.();
        },
      });
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message || error?.response?.data?.title || 'Failed to update school config',
      });
    }
  };

  return {
    updateSchoolConfigHandler,
    isUpdateLoading: mutation.isPending,
    isLoading: mutation.isPending,
  };
};
